import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, Users, FileText, CheckCircle, ArrowRight, Plus } from 'lucide-react';
import api from '../../lib/api';
import { jobUrlSegment } from '../../lib/slug';
import type { Job, KOLProfile } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import { StatNumber, StatsGridSkeleton } from '../../components/ui/StatsLoading';

function statusPill(status: string) {
  const s = status.toUpperCase();
  if (['ACTIVE', 'ACCEPTED', 'APPROVED', 'COMPLETED'].includes(s)) {
    return s === 'COMPLETED' ? 'status-pill status-pill-muted' : 'status-pill status-pill-pos';
  }
  if (['OPEN', 'VIEWED'].includes(s)) return 'status-pill status-pill-info';
  if (['PAUSED', 'PENDING', 'DRAFT'].includes(s)) return 'status-pill status-pill-warn';
  if (['DECLINED', 'REJECTED', 'CANCELLED'].includes(s)) return 'status-pill status-pill-danger';
  return 'status-pill status-pill-muted';
}

export default function CorporateDashboard() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const jobsQuery = useQuery({
    queryKey: ['brand', 'dashboard', 'jobs'],
    queryFn: async () => (await api.get('/jobs')).data as Job[],
    staleTime: 2 * 60 * 1000,
  });

  const kolsQuery = useQuery({
    queryKey: ['brand', 'dashboard', 'kols', 'brand-visible'],
    queryFn: async () => (await api.get('/kols/brand-visible')).data as Pick<KOLProfile, 'id'>[],
    staleTime: 2 * 60 * 1000,
  });

  const jobs = jobsQuery.data ?? [];
  const visibleKolCount =
    kolsQuery.data?.length ??
    Math.max(
      jobs.reduce((sum, j) => sum + (j.acceptedCount || 0), 0),
      jobs.reduce((sum, j) => sum + (j.shortlistedCount || 0), 0),
      jobs.reduce((sum, j) => sum + (j.approvedTalentCount || 0), 0)
    );
  const loadingStats = jobsQuery.isPending || kolsQuery.isPending;
  const refetching = (jobsQuery.isFetching || kolsQuery.isFetching) && !loadingStats;

  const activeJobs = useMemo(
    () => jobs.filter((j) => ['OPEN', 'ACTIVE'].includes(j.status)),
    [jobs]
  );
  const pendingApproval = jobs.filter(
    (j) => j.shortlistedCount >= (j.estimatedKOL || 0) && j.approvedTalentCount === 0
  );

  const stats = [
    { title: 'Active Jobs', value: activeJobs.length, icon: Briefcase, link: '/brand/jobs' },
    { title: 'Pending Approval', value: pendingApproval.length, icon: CheckCircle, link: '/brand/jobs' },
    { title: 'Visible KOLs', value: visibleKolCount, icon: Users, link: '/brand/kols' },
    { title: 'Total Jobs', value: jobs.length, icon: FileText, link: '/brand/jobs' },
  ];

  const firstName = (user?.fullName || 'there').split(' ')[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="page-title text-[1.65rem] sm:text-[1.85rem]">Good day, {firstName}</h1>
          <p className="text-glass-text-2 mt-1 text-sm">Your brand portal — campaigns, talent, and approvals.</p>
        </div>
        <button
          type="button"
          className="btn-accent inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold"
          onClick={() => navigate('/brand/jobs')}
        >
          <Plus className="w-4 h-4" />
          View Campaigns
        </button>
      </div>

      {loadingStats ? (
        <StatsGridSkeleton count={4} className="gap-4" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.title}
                to={stat.link}
                className="glass-card p-5 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-[0.05em] font-medium text-glass-text-3">
                      {stat.title}
                    </p>
                    <div className="stat-display text-[1.85rem] text-glass-text mt-2 min-h-[2.25rem] flex items-center">
                      <StatNumber loading={refetching}>{stat.value}</StatNumber>
                    </div>
                  </div>
                  <div
                    className="w-10 h-10 rounded-[13px] flex items-center justify-center text-white shrink-0"
                    style={{
                      background:
                        'linear-gradient(135deg, rgb(var(--accent-rgb) / 0.9), rgb(var(--accent2-rgb) / 0.7))',
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[0.95rem] font-semibold text-glass-text">Live Campaigns</h2>
          <Link to="/brand/jobs" className="text-xs font-semibold text-accent2 inline-flex items-center gap-1">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loadingStats ? (
          <div className="text-center py-8 text-sm text-glass-text-2">Loading campaigns…</div>
        ) : activeJobs.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-glass-text-2 mb-4 text-sm">No live campaigns yet.</p>
            <button
              type="button"
              className="btn-accent px-4 py-2 text-sm font-semibold"
              onClick={() => navigate('/brand/jobs')}
            >
              Browse Campaigns
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {activeJobs.slice(0, 8).map((job) => {
              const denom = Math.max(job.estimatedKOL || job.shortlistedCount || 1, 1);
              const progress = Math.min(
                100,
                Math.round(((job.approvedTalentCount || 0) / denom) * 100)
              );
              const needsApproval =
                job.shortlistedCount >= (job.estimatedKOL || 0) && job.approvedTalentCount === 0;

              return (
                <button
                  key={job.id}
                  type="button"
                  onClick={() => navigate(`/brand/jobs/${jobUrlSegment(job)}`)}
                  className="w-full text-left p-4 rounded-[14px] border border-glass bg-glass-2 hover:bg-glass-3 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-glass-text truncate">{job.title}</span>
                        <span className={statusPill(job.status)}>{job.status}</span>
                        {needsApproval && (
                          <span className="status-pill status-pill-warn">Needs approval</span>
                        )}
                      </div>
                      <p className="text-xs text-glass-text-3 mt-1">
                        {Array.isArray(job.contentTypes) && job.contentTypes.length > 0
                          ? job.contentTypes.join(', ')
                          : 'Multi-platform'}{' '}
                        · {job.shortlistedCount || 0} shortlisted
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-[rgb(var(--pos))] shrink-0">
                      {job.approvedTalentCount || 0} approved
                    </span>
                  </div>
                  <div className="flex gap-3 mt-3 text-xs font-medium">
                    <span className="text-[rgb(var(--info))]">{job.acceptedCount || 0} accepted</span>
                    <span className="text-accent2">{job.shortlistedCount || 0} shortlist</span>
                    <span className="text-[rgb(var(--warn))]">
                      {Math.max((job.estimatedKOL || 0) - (job.approvedTalentCount || 0), 0)} slots left
                    </span>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-glass-3 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${progress}%`,
                        background:
                          'linear-gradient(90deg, rgb(var(--accent-rgb)), rgb(var(--accent2-rgb)))',
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
